import { useState } from "react";
import { useRouter } from "@tanstack/react-router";
import { signIn, signUp } from "@/lib/auth-client";
import { Button } from "@/components/ui/button";
import { TextField } from "@/routes/stores/-components/fields";

type Mode = "sign-in" | "sign-up";

export function AuthForm() {
  const router = useRouter();
  const [mode, setMode] = useState<Mode>("sign-in");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setPending(true);
    try {
      const result =
        mode === "sign-in"
          ? await signIn.email({ email, password })
          : await signUp.email({ email, password, name });
      if (result.error) {
        setError(result.error.message ?? "Something went wrong.");
        return;
      }
      await router.invalidate();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setPending(false);
    }
  }

  return (
    <form className="space-y-3" onSubmit={onSubmit}>
      {mode === "sign-up" ? (
        <TextField
          id="name"
          label="Name"
          value={name}
          onChange={setName}
          autoComplete="name"
          required
        />
      ) : null}
      <TextField
        id="email"
        label="Email"
        type="email"
        value={email}
        onChange={setEmail}
        autoComplete="email"
        required
      />
      <TextField
        id="password"
        label="Password"
        type="password"
        value={password}
        onChange={setPassword}
        autoComplete={mode === "sign-in" ? "current-password" : "new-password"}
        required
      />

      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}

      <div className="flex items-center justify-between gap-3">
        <Button type="submit" disabled={pending}>
          {pending ? "Please wait" : mode === "sign-in" ? "Sign in" : "Create account"}
        </Button>
        <button
          type="button"
          className="text-sm text-muted-foreground underline-offset-4 hover:underline"
          onClick={() => {
            setMode(mode === "sign-in" ? "sign-up" : "sign-in");
            setError(null);
          }}
        >
          {mode === "sign-in" ? "Need an account? Sign up" : "Have an account? Sign in"}
        </button>
      </div>
    </form>
  );
}
